import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { useApiQuery } from "../../services/api";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { useConversationEngine } from "../../domains/assistant/useConversationEngine";

/**
 * The JuisciGPT landing greeting, drawn from a ROTATING pool rather than the
 * single fixed sentence it used to be (Figma "ROTATIVE PHRASES").
 *
 * Thirteen general phrases are always in the pool, the morning/evening ones
 * only inside their hours. When the user's last question has a subject, the
 * four phrases that name it join them: a greeting that named the subject on
 * every visit read as a narrow product to a clinician whose interests are
 * broader. One is drawn per arrival on the landing — `seed` is owned by the
 * page (which mounts once per arrival) so that focusing the composer, which
 * collapses and re-mounts this greeting, does not re-roll the phrase under the
 * user's eyes.
 *
 * The subject is ALWAYS a JuisciTopic: the one the API stored for the last
 * question, picked after the answer among the topics of the works it cited
 * (`/conversations/latest-topic`, `classifyTurnTopic`). Three earlier sources
 * failed: the LLM title of the last conversation put agent prose in the slot
 * ("New data on Key findings"), a declared interest drawn at random greeted a
 * clinician who had only asked about vaccination with obesity, and the most
 * cited topic named the field around a question rather than its subject. A
 * question that named none of the cited topics, or that is not classified yet,
 * has no subject, and the greeting names none.
 */
export function GPTGreeting({
  seed,
  size,
}: {
  seed: number;
  // `desktop` is the desktop shell's landing (40px, one line over the
  // composer). Chosen by the caller, not by the viewport: a tablet in the
  // phone layout is wide too.
  size: "mobile" | "desktop";
}): React.ReactElement | null {
  const { t } = useTranslation();
  // The last conversation is on the engine that answers new questions.
  const engine = useConversationEngine();
  const topicQuery = useApiQuery(
    "/webapp/private/conversations/latest-topic",
    engine === "legacy" ? {} : false
  );
  const assistantTopicQuery = useApiQuery(
    "/assistant/latest-topic",
    engine === "assistant" ? {} : false
  );

  // Hold the greeting until the answer is in: a phrase drawn from the
  // subject-less pool would be swapped for another one the moment the query
  // lands. The slot it sits in has a fixed height, so nothing moves — the
  // phrase simply fades in once. The cached answer is never stale here: every
  // path that ends a turn or deletes a conversation removes it.
  if (
    engine === "pending" ||
    topicQuery.state === "pending" ||
    assistantTopicQuery.state === "pending"
  )
    return null;

  const latestTopic =
    topicQuery.state === "success"
      ? topicQuery.body.topic
      : assistantTopicQuery.state === "success"
        ? assistantTopicQuery.body.topic
        : null;
  const topicName = latestTopic === null ? "" : getItemTranslation(latestTopic);
  const topic = topicName === "" ? null : topicName;
  const hour = new Date().getHours();

  // Every phrase is a literal `t()` call: the i18n scanner runs with
  // `allowDynamicKeys: false` + `removeUnusedKeys: true`, so a key reached
  // through a variable would be purged from the locale files.
  const anytime = [
    t("Hello. Want to go over a case together?"),
    t("I'm available if you need a second opinion."),
    t("Hesitating on a decision? Let's talk it through."),
    t("A clinical question to work through?"),
    t("Would you like to cross-check that against the evidence?"),
    t("Where do we start?"),
    t("What's still uncertain?"),
    t("Just out of rounds? Tell me more."),
    t("A case that's been on your mind since this morning?"),
    t("Staff meeting coming up? Let's prepare."),
    t("Two options on the table? Let's compare."),
    t("A risk-benefit worth looking into?"),
    t("Would you like to check what the literature says?"),
  ];
  const morning = [
    t("Good morning. What's your first question today?"),
    t("A case to prepare before getting started?"),
    t("A guideline to verify before heading in?"),
  ];
  const evening = [
    t("A point from today that deserves a second look?"),
    t("Good evening. Quick debrief?"),
  ];
  const phrases = [
    ...anytime,
    ...(hour < 12 ? morning : hour < 18 ? [] : evening),
    ...(topic === null
      ? []
      : [
          t("New data on {{topic}}. Shall we take a look?", { topic }),
          t("Would you like to look at {{topic}} from another angle?", {
            topic,
          }),
          t("A question about {{topic}}?", { topic }),
          t("Shall we dig into {{topic}}?", { topic }),
        ]),
  ];
  const phrase = phrases[Math.floor(seed * phrases.length)];
  if (phrase === undefined)
    throw new Error(
      `should not happen: seed ${String(seed)} is outside [0, 1)`
    );

  return (
    <h1
      className={cn(
        "m-0 text-center font-petrona font-medium text-ds-mint-800 animate-fade-in",
        size === "desktop" ? "text-gpt-greeting-desktop" : "text-gpt-greeting"
      )}
    >
      {phrase}
    </h1>
  );
}
